import React from "react";
import * as MUI from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ClearIcon from "@mui/icons-material/Clear";
import { useTranslation } from "react-i18next";
import { ArrowDropDown } from "@mui/icons-material";

const ALL = "ALL";
const SEARCH_DEBOUNCE_MS = 150;

export default function WarehouseFilters({
  search,
  onSearchChange,
  category,
  onCategoryChange,
  categories,
  supplier,
  onSupplierChange,
  suppliers,
  onClearFilters,
}) {
  const { t } = useTranslation();
  const [searchInput, setSearchInput] = React.useState(search);
  const debounceRef = React.useRef(null);

  const DropdownIcon = React.useCallback(
    (props) => (
      <MUI.Tooltip title={t("Open/Close")}>
        <ArrowDropDown
          {...props}
          style={{ ...props.style, pointerEvents: "auto" }}
        />
      </MUI.Tooltip>
    ),
    [t],
  );

  React.useEffect(() => {
    setSearchInput(search);
  }, [search]);

  React.useEffect(() => {
    return () => clearTimeout(debounceRef.current);
  }, []);

  const hasActiveFilters =
    Boolean(search) || category !== ALL || supplier !== ALL;

  function handleSearchInputChange(e) {
    const value = e.target.value;
    setSearchInput(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      onSearchChange(value);
    }, SEARCH_DEBOUNCE_MS);
  }

  function handleClear() {
    clearTimeout(debounceRef.current);
    setSearchInput("");
    onClearFilters();
  }

  return (
    <MUI.Paper
      variant="outlined"
      sx={{ p: 2, mb: 3, borderRadius: 3, borderColor: "divider" }}
    >
      <MUI.Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          gap: 2,
          alignItems: { sm: "center" },
          minWidth: 0,
        }}
      >
        <MUI.TextField
          value={searchInput}
          onChange={handleSearchInputChange}
          placeholder={t("SearchWarehouseItems")}
          size="small"
          fullWidth
          sx={{ flex: 2 }}
          InputProps={{
            startAdornment: (
              <MUI.InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </MUI.InputAdornment>
            ),
          }}
          inputProps={{ "data-testid": "warehouse.items.filters.search-input" }}
        />

        <MUI.FormControl size="small" fullWidth sx={{ flex: 1, minWidth: 0 }}>
          <MUI.InputLabel id="category-select-label">
            {t("Category")}
          </MUI.InputLabel>
          <MUI.Select
            labelId="category-select-label"
            id="category-select"
            value={category}
            label={t("Category")}
            onChange={(e) => onCategoryChange(e.target.value)}
            IconComponent={DropdownIcon}
            SelectDisplayProps={{ "data-testid": "warehouse.items.filters.category-select" }}
          >
            <MUI.MenuItem
              value={ALL}
              data-testid="warehouse.items.filters.category-option.all"
            >
              {t("All")}
            </MUI.MenuItem>
            {categories.map((option) => (
              <MUI.MenuItem
                key={option.id}
                value={String(option.id)}
                data-testid={`warehouse.items.filters.category-option.${option.id}`}
              >
                {option.name}
              </MUI.MenuItem>
            ))}
          </MUI.Select>
        </MUI.FormControl>

        <MUI.FormControl size="small" fullWidth sx={{ flex: 1, minWidth: 0 }}>
          <MUI.InputLabel id="supplier-select-label">
            {t("Supplier")}
          </MUI.InputLabel>
          <MUI.Select
            labelId="supplier-select-label"
            id="supplier-select"
            value={supplier}
            label={t("Supplier")}
            onChange={(e) => onSupplierChange(e.target.value)}
            IconComponent={DropdownIcon}
            SelectDisplayProps={{ "data-testid": "warehouse.items.filters.supplier-select" }}
          >
            <MUI.MenuItem
              value={ALL}
              data-testid="warehouse.items.filters.supplier-option.all"
            >
              {t("All")}
            </MUI.MenuItem>
            {suppliers.map((option) => (
              <MUI.MenuItem
                key={option.id}
                value={String(option.id)}
                data-testid={`warehouse.items.filters.supplier-option.${option.id}`}
              >
                {option.name}
              </MUI.MenuItem>
            ))}
          </MUI.Select>
        </MUI.FormControl>

        <MUI.Tooltip title={t("ClearFilters")}>
          <span>
            <MUI.IconButton
              onClick={handleClear}
              disabled={!hasActiveFilters}
              size="small"
              sx={{ flexShrink: 0 }}
              data-testid="warehouse.items.filters.clear-button"
            >
              <ClearIcon fontSize="small" />
            </MUI.IconButton>
          </span>
        </MUI.Tooltip>
      </MUI.Box>
    </MUI.Paper>
  );
}

export { ALL as WAREHOUSE_FILTER_ALL };
